import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Plus, X } from "lucide-react";

const RANKS = Array.from({ length: 10 }, (_, i) => String(i + 1));

function NameListEditor({ items, onChange, fields, testId }) {
  const update = (idx, key, value) => onChange(items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-1.5">
      {items.map((it, idx) => (
        <div key={idx} className="flex items-center gap-1.5" data-testid={`${testId}-row-${idx}`}>
          {fields.map((f) => (
            <Input key={f.key} placeholder={f.label} value={it[f.key] || ""} onChange={(e) => update(idx, f.key, e.target.value)} className={`bg-black/30 border-white/10 h-7 text-xs ${f.wide ? "flex-1" : "w-20 shrink-0"}`} />
          ))}
          <button onClick={() => remove(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Button data-testid={`${testId}-add`} onClick={add} size="sm" variant="secondary" className="rounded-full gap-1.5 h-7 text-xs"><Plus className="w-3 h-3" /> Add</Button>
    </div>
  );
}

export default function SheetSpellSlotsSection({ character, patchChar }) {
  const slots = character.spell_slots || {};
  const focus = character.focus_points || { max: 0, current: 0 };

  const setSlot = (rank, key, value) => {
    const next = { ...slots, [rank]: { ...(slots[rank] || { max: 0, used: 0 }), [key]: parseInt(value, 10) || 0 } };
    patchChar({ spell_slots: next });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Spell Slots per Day</p>
        <div className="grid grid-cols-5 gap-1.5">
          {RANKS.map((r) => (
            <div key={r} className="bg-black/20 rounded-lg p-1.5 text-center" data-testid={`spell-slot-rank-${r}`}>
              <p className="text-[9px] text-[#D4AF37]/70 uppercase mb-1">Rank {r}</p>
              <Input type="number" min={0} value={slots[r]?.max ?? 0} onChange={(e) => setSlot(r, "max", e.target.value)} placeholder="Max" className="bg-black/30 border-white/10 h-6 text-[10px] text-center mb-1" />
              <Input type="number" min={0} value={slots[r]?.used ?? 0} onChange={(e) => setSlot(r, "used", e.target.value)} placeholder="Used" className="bg-black/30 border-white/10 h-6 text-[10px] text-center" />
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Focus Points</p>
        <div className="flex items-center gap-2">
          <Input data-testid="focus-points-current" type="number" min={0} value={focus.current ?? 0} onChange={(e) => patchChar({ focus_points: { ...focus, current: parseInt(e.target.value, 10) || 0 } })} className="bg-black/30 border-white/10 h-8 w-16 text-center text-xs" />
          <span className="text-[#f1e0c5]/40">/</span>
          <Input data-testid="focus-points-max" type="number" min={0} value={focus.max ?? 0} onChange={(e) => patchChar({ focus_points: { ...focus, max: parseInt(e.target.value, 10) || 0 } })} className="bg-black/30 border-white/10 h-8 w-16 text-center text-xs" />
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Focus Spells</p>
        <NameListEditor testId="focus-spell" items={character.focus_spell_names || []} onChange={(v) => patchChar({ focus_spell_names: v })} fields={[{ key: "name", label: "Focus spell name", wide: true }, { key: "actions", label: "Actions" }]} />
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Innate Spells</p>
        <NameListEditor testId="innate-spell" items={character.innate_spell_names || []} onChange={(v) => patchChar({ innate_spell_names: v })} fields={[{ key: "name", label: "Innate spell name", wide: true }, { key: "type", label: "Type" }]} />
      </div>

      <div>
        <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Rituals</p>
        <NameListEditor testId="ritual" items={character.rituals || []} onChange={(v) => patchChar({ rituals: v })} fields={[{ key: "name", label: "Ritual name", wide: true }, { key: "cast", label: "Cast" }, { key: "cost", label: "Cost" }]} />
      </div>
    </div>
  );
}
